import { useState, useEffect } from "react";
import type { FormEvent, ReactNode } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CircleAlert, House, PartyPopper, Ticket } from "lucide-react";
import { useAuth } from "../hooks/useAuthStore";
import { useStore } from "../store/useStore";
import { Button, PrimaryButton } from "../components/ui/Button";

function InviteShell({
  icon,
  iconTone,
  children,
}: {
  icon: ReactNode;
  iconTone: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12">
      <div className="text-center max-w-md w-full mx-auto px-6">
        <span
          aria-hidden="true"
          className={`inline-flex items-center justify-center w-20 h-20 rounded-[28px] mb-6 ${iconTone}`}
        >
          {icon}
        </span>
        {children}
      </div>
    </div>
  );
}

export default function InvitePage() {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const inviteCode = new URLSearchParams(location.search).get("code");

  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [enteredCode, setEnteredCode] = useState("");

  // Get data from Zustand store
  const joinHouseholdStore = useStore(state => state.joinHousehold);

  const handleJoinHousehold = async () => {
    if (!inviteCode || !user) return;

    setIsJoining(true);
    setError("");

    try {
      await joinHouseholdStore(inviteCode);
      setSuccess(`Successfully joined household!`);

      // Redirect to main app after successful join
      setTimeout(() => {
        history.push("/");
      }, 2000);
    } catch (error) {
      console.error("Error joining household:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to join household. Please check the invite code.";
      setError(errorMessage);
    } finally {
      setIsJoining(false);
    }
  };

  useEffect(() => {
    // If user is not logged in, redirect to login first
    if (!user) {
      localStorage.setItem("pendingInviteCode", inviteCode || "");
      history.push("/login");
      return;
    }

    // Auto-join if we have an invite code
    if (inviteCode && !isJoining) {
      handleJoinHousehold();
    }
  }, [user, inviteCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitCode = (e: FormEvent) => {
    e.preventDefault();
    const code = enteredCode.trim();
    if (!code) return;
    history.replace(`/invite?code=${encodeURIComponent(code)}`);
  };

  if (!inviteCode) {
    return (
      <InviteShell
        icon={<Ticket className="w-10 h-10" />}
        iconTone="bg-accent text-accent-foreground"
      >
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">
          Join a household
        </h1>
        <p className="text-muted-foreground mb-6">
          Enter the invite code you were sent to join your household.
        </p>
        <form onSubmit={handleSubmitCode} className="text-left space-y-4">
          <div>
            <label htmlFor="invite-code" className="field-label">
              Invite code
            </label>
            <input
              id="invite-code"
              type="text"
              value={enteredCode}
              onChange={(e) => setEnteredCode(e.target.value)}
              placeholder="e.g. DEMO123"
              autoComplete="off"
              autoCapitalize="characters"
              className="input-field font-mono tracking-wider"
            />
          </div>
          <PrimaryButton type="submit" disabled={!enteredCode.trim()}>
            Continue
          </PrimaryButton>
          <Button variant="ghost" block onClick={() => history.push("/")}>
            Go to Dashboard
          </Button>
        </form>
      </InviteShell>
    );
  }

  if (success) {
    return (
      <InviteShell
        icon={<PartyPopper className="w-10 h-10" />}
        iconTone="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
      >
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">Welcome!</h1>
        <p role="status" className="text-emerald-700 dark:text-emerald-300 font-semibold mb-2">
          {success}
        </p>
        <p className="text-muted-foreground text-sm">Redirecting to your dashboard...</p>
      </InviteShell>
    );
  }

  if (isJoining) {
    return (
      <InviteShell
        icon={
          <span className="w-10 h-10 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin" />
        }
        iconTone="bg-accent"
      >
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">
          Joining Household...
        </h1>
        <p role="status" className="text-muted-foreground">
          Please wait while we add you to the household.
        </p>
      </InviteShell>
    );
  }

  if (error) {
    return (
      <InviteShell
        icon={<CircleAlert className="w-10 h-10" />}
        iconTone="bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300"
      >
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">Join Failed</h1>
        <p role="alert" className="text-red-600 dark:text-red-300 font-medium mb-6">
          {error}
        </p>
        <div className="space-y-3">
          <PrimaryButton onClick={handleJoinHousehold}>Try Again</PrimaryButton>
          <Button variant="secondary" block onClick={() => history.push("/")}>
            Go to Dashboard
          </Button>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell
      icon={<House className="w-10 h-10" />}
      iconTone="bg-accent text-accent-foreground"
    >
      <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">Join Household</h1>
      <p className="text-muted-foreground mb-6">
        You've been invited to join a household! Click below to join and start tidying together.
      </p>
      <PrimaryButton onClick={handleJoinHousehold}>Join Household</PrimaryButton>
    </InviteShell>
  );
}
